import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VoyahFreeAssistEditor = () => {
  const [loading, setLoading] = useState(true);

  const LANGS = ["En", "Ar", "Ku"];

  const [form, setForm] = useState({
    // ===== STAT 1 =====
    Stat1ValueEn: "", Stat1ValueAr: "", Stat1ValueKu: "",
    Stat1LabelEn: "", Stat1LabelAr: "", Stat1LabelKu: "",

    // ===== STAT 2 =====
    Stat2ValueEn: "", Stat2ValueAr: "", Stat2ValueKu: "",
    Stat2LabelEn: "", Stat2LabelAr: "", Stat2LabelKu: "",

    // ===== STAT 3 =====
    Stat3ValueEn: "", Stat3ValueAr: "", Stat3ValueKu: "",
    Stat3LabelEn: "", Stat3LabelAr: "", Stat3LabelKu: "",

    // ===== MAIN TEXT =====
    DescriptionEn: "", DescriptionAr: "", DescriptionKu: "",
    ButtonTextEn: "", ButtonTextAr: "", ButtonTextKu: "",

    // ===== CARD 1 =====
    Card1TitleEn: "", Card1TitleAr: "", Card1TitleKu: "",
    Card1DescEn: "", Card1DescAr: "", Card1DescKu: "",
    Card1ImageUrl: "",

    // ===== CARD 2 =====
    Card2TitleEn: "", Card2TitleAr: "", Card2TitleKu: "",
    Card2DescEn: "", Card2DescAr: "", Card2DescKu: "",
    Card2ImageUrl: "",

    // ===== BOTTOM IMAGE =====
    BottomImageUrl: ""
  });

  // ================================
  // LOAD 3 LANGUAGES
  // ================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VFAssistSection?lang=en`),
          axios.get(`${API_BASE}/api/VFAssistSection?lang=ar`),
          axios.get(`${API_BASE}/api/VFAssistSection?lang=ku`),
        ]);

        const set = (obj, prefix, enV, arV, kuV) => {
          obj[`${prefix}En`] = enV;
          obj[`${prefix}Ar`] = arV;
          obj[`${prefix}Ku`] = kuV;
        };

        const f = {};

        // ===== STATS =====
        set(f, "Stat1Value", en.data.stats[0].value, ar.data.stats[0].value, ku.data.stats[0].value);
        set(f, "Stat1Label", en.data.stats[0].label, ar.data.stats[0].label, ku.data.stats[0].label);

        set(f, "Stat2Value", en.data.stats[1].value, ar.data.stats[1].value, ku.data.stats[1].value);
        set(f, "Stat2Label", en.data.stats[1].label, ar.data.stats[1].label, ku.data.stats[1].label);

        set(f, "Stat3Value", en.data.stats[2].value, ar.data.stats[2].value, ku.data.stats[2].value);
        set(f, "Stat3Label", en.data.stats[2].label, ar.data.stats[2].label, ku.data.stats[2].label);

        // ===== MAIN TEXT =====
        set(f, "Description", en.data.description, ar.data.description, ku.data.description);
        set(f, "ButtonText", en.data.buttonText, ar.data.buttonText, ku.data.buttonText);

        // ===== CARD 1 =====
        set(f, "Card1Title", en.data.card1.title, ar.data.card1.title, ku.data.card1.title);
        set(f, "Card1Desc", en.data.card1.desc, ar.data.card1.desc, ku.data.card1.desc);
        f.Card1ImageUrl = en.data.card1.img;

        // ===== CARD 2 =====
        set(f, "Card2Title", en.data.card2.title, ar.data.card2.title, ku.data.card2.title);
        set(f, "Card2Desc", en.data.card2.desc, ar.data.card2.desc, ku.data.card2.desc);
        f.Card2ImageUrl = en.data.card2.img;

        // ===== BOTTOM IMAGE =====
        f.BottomImageUrl = en.data.bottomImage;

        setForm(f);
        setLoading(false);
      } catch (err) {
        console.error("Failed:", err);
      }
    };

    load();
  }, []);

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // ================================
  // HANDLE TEXT CHANGE
  // ================================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ================================
  // SAVE TEXT
  // ================================
  const save = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(
        `${API_BASE}/api/VFAssistSection/1`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("Saved!");
    } catch (err) {
      console.error(err);
      alert("Failed to save.");
    }
  };

  // ================================
  // MEDIA UPLOAD
  // ================================
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadKey, setUploadKey] = useState(null);

  const upload = async () => {
    if (!uploadFile) return alert("Select a file first.");

    const fd = new FormData();
    fd.append(uploadKey, uploadFile);

    const token = localStorage.getItem("token");

    try {
      await axios.post(
        `${API_BASE}/api/VFAssistSection/1/media`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Upload success!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  if (loading) return <p>Loading…</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Voyah FREE – Assist Section Editor</h2>

      {/* ======================== */}
      {/* 3 STATS */}
      {/* ======================== */}
      <h3 className="fw-bold mt-3 mb-3">Statistics</h3>

      {[1, 2, 3].map((n) => (
        <div className="card shadow-sm mb-4" key={n}>
          <div className="card-body">
            <h4 className="fw-bold">Stat #{n}</h4>

            <div className="row mt-3">
              {LANGS.map((l) => (
                <div className="col-lg-4 mb-3" key={l}>
                  <div className="border rounded p-3">
                    <h5 className="text-primary">{l} Version</h5>

                    <label className="form-label">Value</label>
                    <input
                      className="form-control mb-2"
                      name={`Stat${n}Value${l}`}
                      value={form[`Stat${n}Value${l}`]}
                      onChange={handleChange}
                    />

                    <label className="form-label">Label</label>
                    <input
                      className="form-control"
                      name={`Stat${n}Label${l}`}
                      value={form[`Stat${n}Label${l}`]}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* ======================== */}
      {/* MAIN TEXT */}
      {/* ======================== */}
      <h3 className="fw-bold mb-3">Main Text</h3>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row">
            {LANGS.map((l) => (
              <div className="col-lg-4 mb-3" key={l}>
                <div className="border p-3 rounded">
                  <h5 className="text-primary mb-3">{l} Version</h5>

                  <label>Description</label>
                  <textarea
                    className="form-control mb-2"
                    rows={4}
                    name={`Description${l}`}
                    value={form[`Description${l}`]}
                    onChange={handleChange}
                  />

                  <label>Button Text</label>
                  <input
                    className="form-control"
                    name={`ButtonText${l}`}
                    value={form[`ButtonText${l}`]}
                    onChange={handleChange}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ======================== */}
      {/* CARD 1 + CARD 2 */}
      {/* ======================== */}
      {[1, 2].map((n) => (
        <div className="card shadow-sm mb-4" key={n}>
          <div className="card-body">
            <h4 className="fw-bold mb-3">Card #{n}</h4>

            {/* IMAGE */}
            <img
              src={resolveUrl(form[`Card${n}ImageUrl`])}
              className="w-100 rounded mb-3"
              alt=""
              style={{ maxHeight: "250px", objectFit: "cover" }}
            />

            <button
              className="btn btn-primary mb-4"
              onClick={() => {
                setUploadKey(`Card${n}Image`);
                setShowModal(true);
              }}
            >
              Upload / Replace Image
            </button>

            {/* LANG */}
            <div className="row">
              {LANGS.map((l) => (
                <div className="col-lg-4 mb-3" key={l}>
                  <div className="border rounded p-3">
                    <h5 className="text-primary">{l} Version</h5>

                    <label>Title</label>
                    <input
                      className="form-control mb-2"
                      name={`Card${n}Title${l}`}
                      value={form[`Card${n}Title${l}`]}
                      onChange={handleChange}
                    />

                    <label>Description</label>
                    <textarea
                      rows={4}
                      className="form-control"
                      name={`Card${n}Desc${l}`}
                      value={form[`Card${n}Desc${l}`]}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* ======================== */}
      {/* BOTTOM IMAGE */}
      {/* ======================== */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h4 className="fw-bold">Bottom Image</h4>

          <img
            src={resolveUrl(form.BottomImageUrl)}
            className="w-100 rounded mb-3"
            style={{ maxHeight: "260px", objectFit: "cover" }}
          />

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadKey("BottomImage");
              setShowModal(true);
            }}
          >
            Upload / Replace
          </button>
        </div>
      </div>

      {/* SAVE BUTTON */}
      <div className="text-end">
        <button className="btn btn-primary px-4" onClick={save}>
          Save All
        </button>
      </div>

      {/* ======================== */}
      {/* MODAL */}
      {/* ======================== */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Upload Image</h5>
                  <button className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-primary" onClick={upload}>
                    Upload
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VoyahFreeAssistEditor;
